import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';

import React, { useState } from 'react';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { VideoFeatures, PixelTracking } from '@/store/videoFeatures';
import { t } from '@/utils/i18n';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Activity, Facebook, Key, Tag, Globe, Video, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { SAMPLE_VIDEOS } from '@/pages/dashboard/sampleData';

interface PixelConfigProps {
  features: VideoFeatures;
  setFeature: <K extends keyof VideoFeatures>(key: K, value: VideoFeatures[K]) => void;
}

export const PixelConfig: React.FC<PixelConfigProps> = ({
  features,
  setFeature,
}) => {
  useLanguage();
  const [siteUrlError, setSiteUrlError] = useState<string>('');

  // Get current pixel data, ensuring it's a single object
  const getCurrentPixel = (): PixelTracking => {
    if (!features.pixelTracking) {
      return {
        enabled: false,
        pixelType: 'facebook',
        pixelId: ''
      };
    }
    
    if (Array.isArray(features.pixelTracking)) {
      return features.pixelTracking[0] || {
        enabled: false,
        pixelType: 'facebook',
        pixelId: ''
      };
    }
    
    return features.pixelTracking;
  };
  
  const pixelData = getCurrentPixel();

  const handlePixelEnabledChange = (enabled: boolean) => {
    const updatedPixel = {
      ...pixelData,
      enabled
    };
    
    setFeature('pixelTracking', updatedPixel);
  };

  const handleTokenChange = (token: string) => {
    setFeature('pixelTracking', {
      ...pixelData,
      token
    });
  };

  const handlePixelIdChange = (pixelId: string) => {
    setFeature('pixelTracking', {
      ...pixelData,
      pixelId
    });
  };

  const handleSiteUrlChange = (siteUrl: string) => {
    if (siteUrl && !siteUrl.startsWith('https://')) {
      setSiteUrlError(t('pixel.siteUrl.error'));
    } else {
      setSiteUrlError('');
    }
    setFeature('pixelTracking', {
      ...pixelData,
      siteUrl
    });
  };

  const handleVideoChange = (videoId: string) => {
    setFeature('pixelTracking', {
      ...pixelData,
      videoId
    });
  };

  const handleCopyToken = () => {
    if (pixelData.token) {
      navigator.clipboard.writeText(pixelData.token);
      toast.success(translateInterfaceText("Token copiado!"), { position: 'top-center' });
    }
  };

  const videoOptions = SAMPLE_VIDEOS.map(video => ({
    value: video.id,
    label: video.title
  }));

  return (
    <div className="space-y-6 px-6 py-4">
      {/* Header */}
      <div className="space-y-1">
        <h3 className="text-lg font-semibold">{t('pixel.header.title')}</h3>
        <p className="text-sm text-muted-foreground">{t('pixel.header.subtitle')}</p>
      </div>

      {/* Enable Switch */}
      <div className="flex items-center justify-between p-4 rounded-[12px] border border-[#E4E7EC] bg-white">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Activity className="h-5 w-5 text-primary" />
          </div>
          <div>
            <Label className="text-sm font-medium">{t('enablePixel')}</Label>
            <p className="text-xs text-muted-foreground">{t('pixel.enable.helper')}</p>
          </div>
        </div>
        <Switch 
          checked={pixelData?.enabled || false} 
          onCheckedChange={handlePixelEnabledChange}
        />
      </div>
      
      {/* Configuration Fields */}
      {pixelData?.enabled && (
        <div className="space-y-4 p-4 rounded-[12px] border border-[#E4E7EC] bg-white">
          {/* Pixel Type - Fixed as Facebook */}
          <div className="space-y-2">
            <Label className="text-sm flex items-center gap-2">
              <Facebook className="h-4 w-4" />
              {t('pixelType')}
            </Label>
            <div className="p-3 rounded-md border bg-background">
              <span className="text-sm font-medium">{translateInterfaceText("Facebook")}</span>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('pixel.type.helper')}
            </p>
          </div>

          {/* Token */}
          <div className="space-y-2">
            <Label className="text-sm flex items-center gap-2">
              <Key className="h-4 w-4" />
              {t('pixel.token')}
            </Label>
            <div className="flex gap-2">
              <Input 
                type="password"
                value={pixelData.token || ''}
                onChange={(e) => handleTokenChange(e.target.value)}
                placeholder={t('pixel.token.placeholder')}
              />
              <Button 
                variant="outline" 
                size="icon"
                onClick={handleCopyToken}
                disabled={!pixelData.token}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">{t('pixel.token.helper')}</p>
          </div>

          {/* Pixel ID */}
          <div className="space-y-2">
            <Label className="text-sm flex items-center gap-2">
              <Tag className="h-4 w-4" />
              {t('pixelId')}
            </Label>
            <Input 
              value={pixelData.pixelId || ''}
              onChange={(e) => handlePixelIdChange(e.target.value)}
              placeholder={translateInterfaceText("Ex: 1234567890123456")}
            />
            <p className="text-xs text-muted-foreground">{t('pixel.id.helper')}</p>
          </div>

          {/* Site URL */}
          <div className="space-y-2">
            <Label className="text-sm flex items-center gap-2">
              <Globe className="h-4 w-4" />
              {t('pixel.siteUrl')}
            </Label>
            <Input 
              value={pixelData.siteUrl || ''}
              onChange={(e) => handleSiteUrlChange(e.target.value)}
              placeholder={t('pixel.siteUrl.placeholder')}
            />
            {siteUrlError && (
              <p className="text-xs text-red-500">{siteUrlError}</p>
            )}
            <p className="text-xs text-muted-foreground">{t('pixel.siteUrl.helper')}</p>
          </div>

          {/* Video Selection */}
          <div className="space-y-2">
            <Label className="text-sm flex items-center gap-2">
              <Video className="h-4 w-4" />
              {t('pixel.selectVideo')}
            </Label>
            <Select 
              value={pixelData.videoId || ''} 
              onValueChange={handleVideoChange}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('pixel.selectVideo.placeholder')} />
              </SelectTrigger>
              <SelectContent>
                {videoOptions.map((video) => (
                  <SelectItem key={video.value} value={video.value}>
                    {video.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Success Indicator */}
          {pixelData.token && pixelData.pixelId && (
            <div className="p-3 rounded-md border bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800">
              <p className="text-xs text-green-700 dark:text-green-300">
                {t('pixel.configured')}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
